<template>
    <span class="fi fa-c"
        :class="[isEmpty ? 'el--empty' : '', isTrendLeft ? 'fh-r' : 'fh']"
        style="flex-shrink: 0;">
        <template v-if="!isEmpty">
            <span class="ath-visual-number-basic-value"
                :class="[sentimentCname, hasColorVars ? 'el--color' : '']"
                :style="colorVars">{{ value }}</span>
            <Trend v-if="trend !== false"
                :value="trend"
                :positive-color="positiveColor"
                :negative-color="negativeColor"
                :class="[isTrendLeft ? 'sm-r-xs' : 'sm-l-xs']"
                :colorize="1" />
        </template>
        <span v-else>{{ fallback }}</span>
    </span>
</template>

<script>
import { isNeString, isNumber } from '@aretheyhappy/utils';
import Trend from './Trend.vue';

export default {
    name: 'mead-number-basic',
    props: {
        /**
         * Trend value to render via the `Trend` indicator (colorized using the
         * standard positive/negative scheme). Defaults to `false`, which hides
         * the trend indicator.
         */
        trend: {type: [Number, Boolean], default: false},
        /**
         * Side of the value the trend indicator sits on: "left" or "right".
         */
        trendPosition: {type: String, default: 'right'},
        /**
         * When true, colors the value text with the sentiment color matching the trend
         * (positive when the trend is above 0, negative when below). Has no effect without
         * a numeric `trend`, or when the trend is 0.
         */
        sentiment: {type: Boolean, default: false},
        /**
         * Color used for a positive trend, on both the trend arrow and (with `sentiment`) the value
         * text: a COLOR_STYLES value (e.g. "primary"), a tint of one (e.g. "primary-20") or any CSS
         * color. Defaults to the positive sentiment color.
         */
        positiveColor: {type: String, default: ''},
        /**
         * Color used for a negative trend, on both the trend arrow and (with `sentiment`) the value
         * text: a COLOR_STYLES value (e.g. "primary"), a tint of one (e.g. "primary-20") or any CSS
         * color. Defaults to the negative sentiment color.
         */
        negativeColor: {type: String, default: ''},
        /**
         * Value to render when `value` is empty (not a number or non-empty string).
         */
        fallback: {type: [Number, String], default: () => 0},
        /**
         * The value to display. When neither a number nor a non-empty string, the
         * component is considered empty and shows `fallback` instead.
         */
        value: {type: [Number, String, Boolean], default: false},
    },
    components: {Trend},
    computed: {
        isEmpty: function () {
            return !isNumber(this.value) && !isNeString(this.value);
        },
        isTrendLeft: function () {
            return this.trendPosition === 'left';
        },
        sentimentCname: function () {
            if (!this.sentiment || !isNumber(this.trend) || this.trend === 0) return '';
            return this.trend > 0 ? 'el--t-positive' : 'el--t-negative';
        },
        colorVars: function () {
            return {
                ...(isNeString(this.positiveColor) ? MeadCommon.colorVars(this.positiveColor, {bg: '--number-basic-positive'}) : {}),
                ...(isNeString(this.negativeColor) ? MeadCommon.colorVars(this.negativeColor, {bg: '--number-basic-negative'}) : {}),
            };
        },
        hasColorVars: function () {
            return Object.keys(this.colorVars).length > 0;
        },
    },
};
</script>

<style lang="scss" scoped>
.ath-visual-trend.fas-r {
    transform: translateY(-.4rem);
}

.el--empty {
    opacity: .25;
}

.ath-visual-number-basic-value {
    font-variant-numeric: tabular-nums;
    
    &.el--t-positive {
        color: var(--c-sentiment-positive-bg);
    }

    &.el--t-negative {
        color: var(--c-sentiment-negative-bg);
    }

    //  Colors from the `positiveColor`/`negativeColor` props
    &.el--color.el--t-positive {
        color: var(--number-basic-positive, var(--c-sentiment-positive-bg));
    }

    &.el--color.el--t-negative {
        color: var(--number-basic-negative, var(--c-sentiment-negative-bg));
    }
}
</style>
